import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getToolBySlug, getRelatedTools } from "@/data/tools";
import ToolRunner from "@/tools/ToolRunner";
import Breadcrumb from "@/components/Breadcrumb";
import AdSlot from "@/components/AdSlot";
import SEO from "@/components/SEO";
import { useI18n } from "@/context/I18nContext";
import { useFavorites } from "@/context/FavoritesContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Star, Share2, Twitter, Facebook, Linkedin, Copy, Check, Heart } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { TOOL } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ToolPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const { isFav, toggle } = useFavorites();
  const tool = getToolBySlug(slug);
  const [rating, setRating] = useState(0);
  const [avgRating, setAvgRating] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!tool) return;
    axios.post(`${API}/tools/view`, { tool_slug: slug }).catch(() => {});
  }, [slug, tool]);

  if (!tool) { nav("/404", { replace: true }); return null; }

  const related = getRelatedTools(slug);
  const Icon = tool.icon;
  const url = typeof window !== "undefined" ? window.location.href : "";
  const fav = isFav(slug);

  const rate = async (r) => {
    setRating(r);
    try {
      const res = await axios.post(`${API}/tools/rate`, { tool_slug: slug, rating: r });
      setAvgRating(res.data.avg_rating);
      toast.success(t("tool.rate_thanks"));
    } catch { /* noop */ }
  };

  const copyLink = async () => { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const faqs = [
    { q: `Is ${tool.name} free?`, a: `Yes, ${tool.name} is completely free with no signup or watermarks.` },
    { q: `Does ${tool.name} upload my files?`, a: `No. All processing happens locally in your browser — your images stay on your device.` },
    { q: `Which browsers are supported?`, a: `${tool.name} works on all modern browsers including Chrome, Firefox, Safari, and Edge on both desktop and mobile.` },
    { q: `Is there a file size limit?`, a: `Processing runs locally so limits depend on your device's memory. Most images up to 20MB work smoothly.` },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title={`${tool.name} — Free Online ${tool.name} | ImageKitHub`}
        description={`${tool.desc} 100% free, private, and runs in your browser.`}
        canonical={url}
        jsonLd={[
          { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
            { "@type": "ListItem", position: 2, name: "Tools", item: `${window.location.origin}/tools` },
            { "@type": "ListItem", position: 3, name: tool.name, item: url },
          ]},
          { "@context": "https://schema.org", "@type": "SoftwareApplication", name: tool.name, applicationCategory: "MultimediaApplication", operatingSystem: "Any", offers: { "@type": "Offer", price: "0" } },
          { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
        ]}
      />

      <Breadcrumb items={[{ label: t("nav.tools"), to: "/tools" }, { label: tool.name }]} />

      <div className="mt-6 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 grid place-items-center">
          <Icon size={22} />
        </div>
        <div className="flex-1">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white">{tool.name}</h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">{tool.desc}</p>
        </div>
        <button
          onClick={() => toggle(slug)}
          data-testid="tool-page-fav"
          aria-label={fav ? t("tool.unfavorite") : t("tool.favorite")}
          className={`p-2.5 rounded-lg border border-slate-200 dark:border-white/10 transition-colors ${fav ? "text-red-500" : "text-slate-400 hover:text-red-500"}`}
        >
          <Heart size={18} fill={fav ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-10">
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#121316] p-6 sm:p-8">
            <ToolRunner tool={tool} />
          </div>

          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">{t("tool.how_title")}</h2>
            <ol className="mt-4 space-y-3">
              {[t("tool.how_1"), t("tool.how_2"), t("tool.how_3"), t("tool.how_4")].map((s, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-blue-600 text-white text-xs grid place-items-center">{i + 1}</span>
                  <span className="text-slate-600 dark:text-slate-400 leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
          </div>

          <AdSlot label="Sponsored" />

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl border border-slate-200 dark:border-white/10">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t("tool.features_title")}</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li>• {t("tool.feature_1")}</li>
                <li>• {t("tool.feature_2")}</li>
                <li>• {t("tool.feature_3")}</li>
                <li>• {t("tool.feature_4")}</li>
              </ul>
            </div>
            <div className="p-6 rounded-2xl border border-slate-200 dark:border-white/10">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t("tool.benefits_title")}</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li>• {t("tool.benefit_1")}</li>
                <li>• {t("tool.benefit_2")}</li>
                <li>• {t("tool.benefit_3")}</li>
                <li>• {t("tool.benefit_4")}</li>
              </ul>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">{t("tool.faq_title")}</h2>
            <Accordion type="single" collapsible className="mt-4">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-slate-200 dark:border-white/10">
                  <AccordionTrigger className="text-left text-slate-900 dark:text-white">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 dark:text-slate-400">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="p-8 rounded-2xl border-2 border-dashed border-slate-200 dark:border-white/10 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t("tool.comments_placeholder")}</p>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-6 bg-white dark:bg-[#121316]">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-3">{t("tool.rate_title")}</h3>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map((n) => (
                <button key={n} onClick={() => rate(n)} data-testid={`${TOOL.ratingStar}-${n}`} aria-label={`Rate ${n}`} className="p-1">
                  <Star size={20} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300 dark:text-slate-600"} />
                </button>
              ))}
            </div>
            {avgRating != null && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t("tool.community_avg")}: {avgRating}★</p>}
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-6 bg-white dark:bg-[#121316]">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-3 flex items-center gap-2"><Share2 size={14} /> {t("tool.share_title")}</h3>
            <div className="flex items-center gap-2">
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(tool.name)}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-slate-200 dark:border-white/10 hover:text-blue-600"><Twitter size={16} /></a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-slate-200 dark:border-white/10 hover:text-blue-600"><Facebook size={16} /></a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-slate-200 dark:border-white/10 hover:text-blue-600"><Linkedin size={16} /></a>
              <button onClick={copyLink} className="p-2 rounded-lg border border-slate-200 dark:border-white/10 hover:text-blue-600" aria-label="Copy link">{copied ? <Check size={16} /> : <Copy size={16} />}</button>
            </div>
          </div>

          <AdSlot label="Sponsored" />

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-3">{t("tool.related_title")}</h3>
            <div className="space-y-3">
              {related.map((tt) => (
                <Link key={tt.slug} to={`/tools/${tt.slug}`} className="block p-4 rounded-xl border border-slate-200 dark:border-white/10 hover:border-blue-500/40 transition-colors">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{tt.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{tt.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
